/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect } from 'react';
import { useState } from 'react';
import {useSelector} from 'react-redux';
import {useTable, useSortBy, usePagination } from "react-table";
import { OrderTableProps } from '../../lib/Interfaces/OrderTableProps';
import { ApplicationState } from '../../store';
import {useNavigate} from 'react-router-dom';
import { Form, Box, GridContainer, GridItem, InlineInputs, Textbox, Pod, Button, ButtonBar, DateInput, FilterableSelect, Icon, Heading, Option} from '../../lib/carbon-ui';
import axiosService from '../../lib/Axios';
import {AxiosError, AxiosResponse} from "axios";
import { store } from '../..';
import './RequestPage.css';
import DefaultMessage, { IMessageOrToastOptions } from '../../Components/MessageOrToast/DefaultMessage'
import { DropDown } from '../../lib/Interfaces/DropDown';
import { OrderItem } from '../../lib/Interfaces/OrderItem';
import GetDepotsForDropDownResponse from '../../lib/Interfaces/GetDepotsForDropDownResponse';
import { MaxOptraInterface } from '../../lib/Interfaces/MaxOptraInterface';
import {Errors} from '../../lib/Interfaces/ErrorsBody';

export const returnDistinctOrderNumbersFromOrderItemsCollection = (orderItem: OrderItem[]) => {
  const allOrderNumbers = orderItem.map((el) => el.orderReferenceNumber);
  return [...Array.from(new Set(allOrderNumbers))].join(", ")
  }

const RequestPage = () => {
  const navigate = useNavigate();
    
  //Whether the user has logged in using the RF staff table
  const userIsAuthenticated = useSelector((state: ApplicationState) => state.authenticationState.authSettings?.isAuthenticated);
  
  //Date from the date selector box
  const [dateFrom, setDateFrom] = useState<string>(new Date().toLocaleDateString('en-UK'));
  const currentDate = new Date();
  currentDate.setDate(currentDate.getDate()+1);
  const [dateTo, setDateTo] = useState<string>(new Date(currentDate).toLocaleDateString('en-UK'));
  
  // Get the Depots and set the default
  const [selectedDepot, setSelectedDepot] = useState<string>("All Depots");
  const [allDepots, setAllDepots] = useState<DropDown[]>();
  
  const [showGettingRealitexMessage, setShowGettingRealitexMessage] = useState<boolean>(false);
  const [showUploadingToMaxOptraMessage, setShowUploadingToMaxOptraMessage] = useState<boolean>(false);
  const [showSuccessTable, setShowSuccessTable] = useState<boolean>(false);
  
  //Text of clickable button
  const [importJobsButtonText, setImportJobsButtonText] = useState<string>("Import Drops From Realitex");
  const [uploadJobsButtonText, setUploadJobsButtonText] = useState<string>("Upload Drops To MaxOptra");
  const [disableImportJobsButton, setDisableImportJobsButton] = useState(false);
  const [disableUploadJobsButton, setDisableUploadJobsButton] = useState(true);

  const [listOfAllOrdersPulledIn, setListOfAllOrdersPulledIn] = useState<MaxOptraInterface[]>([]);
  const [listOfAllOrdersErrorsPulledIn, setListOfAllOrdersErrorsPulledIn] = useState<MaxOptraInterface[]>([]);
  const [listOfAllErrorsFromOrderCreation, setListOfAllErrorsFromOrderCreation] = useState<Errors[]>([]);
  
  //State for DefaultMessage box used to display warnings.errors
  const [messageBoxIsOpen, setMessageBoxIsOpen] = useState<boolean>(false);
  const [messageBoxVariant, setMessageBoxVariant] = useState<string>("success");
  const [messageBoxTitle, setMessageBoxTitle] = useState<string>("");
  const [messageBoxMessage, setMessageBoxMessage] = useState<string>("");
  
  const errorMessageBoxOptions: IMessageOrToastOptions = {
    isOpen: messageBoxIsOpen,
    variant: messageBoxVariant,
    title: messageBoxTitle,
    message: messageBoxMessage,
  }

  function sleep(seconds: number) 
  {
  var e = new Date().getTime() + (seconds * 1000);
  while (new Date().getTime() <= e) {}
  }
  
  // An empty dependancy array isn't needed in React version 18 on useEffect hooks
  useEffect(() => {
    if (!userIsAuthenticated) navigate("/");
      // ------------------------------------------------------------------
      // Get list of deopts for the dropdown control
      // ------------------------------------------------------------------
    
      axiosService
      .get(`api/v1/Depot/DepotsForDropDown`) 
      .then(function (response: AxiosResponse<GetDepotsForDropDownResponse>) { 
        const {data} = response;

        // Add 'All Depots' to start of array
        data.depots.unshift({value:"All Depots", key:"All Depots", text:"All Depots"});
        setAllDepots(data.depots);
      });
  }, [])
    
  const handleChangedDepot = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedDepot(event.target.value);
  }

  const resetState = () => {
    setMessageBoxVariant("success");
    setMessageBoxTitle("");
    setMessageBoxMessage("");
    setMessageBoxIsOpen(false);
    setImportJobsButtonText("Import Drops From Realitex");
    setUploadJobsButtonText("Upload Drops To MaxOptra")
    setDisableImportJobsButton(false);
    setDisableUploadJobsButton(true);
    setListOfAllErrorsFromOrderCreation([]);
    setListOfAllOrdersErrorsPulledIn([]);
    setShowGettingRealitexMessage(false);
    setShowUploadingToMaxOptraMessage(false);
    setShowSuccessTable(false);
  }
  
  const onChangeSetDateFrom = ({target} : {target: any}) => {
      setDateFrom(target.value.formattedValue);
      resetState();
    };

  const onChangeSetDateTo = ({target} : {target: any}) => {
      setDateTo(target.value.formattedValue);
      resetState();
    };

  // ------------------------------------------------------------------
  // This is the Axios POST request for grabbing jobs from realitex
  // ------------------------------------------------------------------
  const handleGetOrders = (event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent>) => 
    {
    event.preventDefault();
    resetState();
    setShowGettingRealitexMessage(true);

    // *** FOR TESTING ONLY SO WE CAN SEE THE GETTING REALITEX MESSAGE ***
    //sleep(5);

    axiosService.post(`api/v1/MaxOptra/GetOrdersFromRealitex?`, 
      {
        //PROPS
        depot: selectedDepot === "ALL" ? "" : selectedDepot, 
        dateTo: dateTo,
        dateFrom: dateFrom
      })
      .then(function(response: AxiosResponse<MaxOptraInterface[]>) {
        //handle successful request
        const { data } = response;

        if (!data) {
          setMessageBoxIsOpen(true);
          setMessageBoxVariant("error");
          setMessageBoxTitle("Error getting drops");
          setMessageBoxMessage("There was an error getting the drops. Please try again later or contact Innov8 for assistance.");
        } else if (data.length === 0) {
          setMessageBoxIsOpen(true);
          setMessageBoxVariant("warning");
          setMessageBoxTitle("No drops could be found");
          setMessageBoxMessage("There were no drops found for the specified date. If you think there should have been, please contact Innov8.");
        } else {
          // Check if any orders have errors
          const hasErrors = data.some(order => order.errors && order.errors.length > 0);
  
          if (hasErrors) {
              // Handle the case where orders have errors
              setMessageBoxIsOpen(false);
              setShowSuccessTable(true);
              setMessageBoxIsOpen(true);
              setMessageBoxVariant("error");
              setMessageBoxTitle("One or more errors were detected");
              setMessageBoxMessage("Please check the error details before proceeding.");
          } else {
              setMessageBoxIsOpen(false);
              setShowSuccessTable(true);
          }
        }

        const returndropWindowIsValid = (dropWindow: string) => {
          return !dropWindow || dropWindow === '' || dropWindow.length !== 5 || !dropWindow.includes(":") ? false : true
          }

        const { validData, dataThatErrored } = (data || []).reduce<{
          validData: MaxOptraInterface[];
          dataThatErrored: MaxOptraInterface[];
          }>(
            (acc, transportExtractRecord) => {
             if (!transportExtractRecord || !transportExtractRecord.orderItems) {
               acc.dataThatErrored.push(transportExtractRecord);
             } else {
               acc.validData.push(transportExtractRecord);
             }
             return acc;
          } ,
            { validData: [], dataThatErrored: [] }
        );
        
        const dataToCommitToState = validData.map((transportExtractRecord, index) => {
          transportExtractRecord.firstOrderItem = transportExtractRecord.orderItems
            .map(oi => oi.orderReferenceNumber ?? "")
            .join(', ');

          return transportExtractRecord;
        });

        setShowGettingRealitexMessage(false);

        setListOfAllOrdersErrorsPulledIn(dataThatErrored);
        setListOfAllOrdersPulledIn(dataToCommitToState); 

        if (dataThatErrored.length > 0)
        {
          setMessageBoxIsOpen(true);
          setMessageBoxVariant("warning");
          setMessageBoxTitle("Some drops could not be processed")
          setMessageBoxMessage("At least one drop has an error and should be reviewed before proceeding.");
        }

        if (dataToCommitToState.length > 0)
        {
          setDisableUploadJobsButton(false);
        }
      })
      .catch(function (error) {
        //catch any errors and handle them
        setMessageBoxIsOpen(true);
        setMessageBoxVariant("error");
        setMessageBoxTitle("Error getting drops")
        setMessageBoxMessage("There was an error getting the drops. Please try again later or contact Innov8 for assistance.")     
      });
  };

  // ------------------------------------------------------------------
  // Upload drops to MaxOptra
  // ------------------------------------------------------------------
  const handleUploadOrders = (
    event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent>
  ) => {
    resetState();
    setShowUploadingToMaxOptraMessage(true);

    axiosService.post(`api/v1/MaxOptra/SubmitOrdersToMaxOptra`, listOfAllOrdersPulledIn
    )
    .then(function(response:AxiosResponse<boolean>){
      const {data} = response;
      if (data){
        setMessageBoxIsOpen(true);
        setMessageBoxVariant("success");
        setMessageBoxTitle("Posting Success");
        setMessageBoxMessage(listOfAllOrdersPulledIn.length + " drops have been posted to MaxOptra");
      }
      else {
        setMessageBoxIsOpen(true);
        setMessageBoxVariant("");
        setMessageBoxTitle("Error posting drops")
        setMessageBoxMessage("There was an error posting the drops. Please try again later or contact Innov8 for assistance.") 
      }
      setShowUploadingToMaxOptraMessage(false);
    })
    .catch(function (response:AxiosError<Errors[]>) {
      //catch any errors and handle them
      const listOfAllErrorsFromOrderCreation = response.response?.data;
      setMessageBoxIsOpen(true);
      setMessageBoxVariant("error");
      setMessageBoxTitle("Error: drops")
      setMessageBoxMessage("There was an error uploading the drops. Please try again later or contact Innov8 for assistance.")     

      if (listOfAllErrorsFromOrderCreation){
        setListOfAllErrorsFromOrderCreation(listOfAllErrorsFromOrderCreation);
      }
      setShowUploadingToMaxOptraMessage(false);
    });
  }
    
  const Table: React.FC<OrderTableProps> = ({columns, data}) => {
      const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        //rows,
        prepareRow,      
        page,      
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state: {pageIndex, pageSize },
      } = useTable({
        columns, 
        data,
        initialState: {pageIndex: 0},
      },
        useSortBy,
        usePagination
      ); 
      return(
        <>
          <table width="100%" {...getTableProps()}>
            <thead>
              {headerGroups.map((headerGroup) => (
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column) => (
                    <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                    {column.render("Header")}  
                    <span>
                        {column.isSorted
                          ? column.isSortedDesc
                            ? " 🔽"
                            : " 🔼"
                          : ""}
                      </span>
                    </th>
                  ))}            
                </tr>
              ))}
            </thead>
    
            <tbody {...getTableBodyProps()}>
              {page.map((row, i) => {
                prepareRow(row);
                return (
                  <tr {...row.getRowProps()}>
                    {row.cells.map((cell) => {
                      return (
                        <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
          <br/>
          <div className="pagination">
          <button onClick={() => gotoPage(0)} disabled={!canPreviousPage}>
            {'<<'}
          </button>{' '}
          {/* Commented out because these 2 buttons redirect to the login page 
          <button onClick={() => previousPage()} disabled={!canPreviousPage}>
            {'<'}
          </button>{' '}
          <button onClick={() => nextPage()} disabled={!canNextPage}>
            {'>'}
          </button>{' '} */}
          <button onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage}>
            {'>>'}
          </button>{' '}
          <span>
            Page{' '}
            <strong>
              {pageIndex + 1} of {pageOptions.length}
            </strong>{' '}
          </span>
          <span>
            | Go to page:{' '}
            <input
              type="number"
              defaultValue={pageIndex + 1}
              onChange={e => {
                const page = e.target.value ? Number(e.target.value) - 1 : 0
                gotoPage(page)
              }}
              style={{ width: '100px' }}
            />
          </span>{' '}
          <select
            value={pageSize}
            onChange={e => {
              setPageSize(Number(e.target.value))
            }}
          >
            {[10, 20, 30, 40, 50].map(pageSize => (
              <option key={pageSize} value={pageSize}>
                Show {pageSize}
              </option>
            ))}
          </select>
        </div>      
        </>
      );
    };
  
    const errorTableColumns = React.useMemo(    
      () => [
        {
          Header: "Depot",
          accessor: "distributionCentreReference",
        },
        {
          Header: "Account Number",
          accessor: "clientName",
        },
        {
          Header: "Customer",
          accessor: "customerLocation.customerName",
        },
        {
          Header: "Drop Window",
          //accessor: "timeWindows[0].start",
          accessor: (row: any) => {
            if(row.start && row.end !== ''){
    
              return (
                <>{row.start + '/' + row.end}</>
              )
            }
            else{
              return ''
            }
           
          },
        },
        {
          Header: "Delivery Reference",
          accessor: "referenceNumber",
        },
        {
          Header: "Realitex Order Number",
          accessor: "firstOrderItem"
        }
        
      ],
      []
    );
    const ErrorResponseOrderCreationColumns= React.useMemo(
      () => [ 
        {
          Header: "Code",
          accessor: "code",
        },
        {
          Header: "Name",
          accessor: "name",
        },
        {
          Header: "Details",
          accessor: "details",
        },
        {
          Header: "Field",
          accessor: "field",
        }
      ],
      []
    );

    const ErrorResponseColumns = React.useMemo(
      () => [ 
        {
          Header: "Code",
          accessor: "referenceNumber",
        },
        {
          Header: "Name",
          accessor: "errors[0].name",
        },
        {
          Header: "Details",
          accessor: "errors[0].details",
        },
        {
          Header: "Field",
          accessor: "errors[0].field",
        }
      ],
      []
    );

    const MainResponseColumns = React.useMemo(
      () => [
          {
            Header: "Depot",
            accessor: "distributionCentreReference",
          },
          {
            Header: "Account Number",
            accessor: "clientName"
          },
          {
            Header: "Customer",
            accessor: "customerLocation.customerName",
          },
          {
            Header: "Drop Window",
            accessor: (row: any) => {
              if(row.timeWindows[0].start && row.timeWindows[0].end !== ''){
                const endDateTime = new Date(row.timeWindows[0].end);
                const endFormatted =
                `
                ${endDateTime.getHours().toString().padStart(2,
                '0'
                )}
                :
                ${endDateTime.getMinutes().toString().padStart(2,
                '0'
                )}
                `
                ;
                return (
                  <>{row.timeWindows[0].start + '/' + endFormatted}</>
                )
              }
              else{
                return ''
              }
              
            },
          },
          {
            Header: "Delivery Reference",
            accessor: "referenceNumber",
          },
          {
            Header: "Realitex Order Number",
            accessor: "firstOrderItem",
          }
          
        ],
        []
    ); 
  
    return (    
      <Form 
        onSubmit={() => {}} 
        fieldSpacing={4}
        >
          
        <Heading title = "Upload To MaxOptra" mt={2}></Heading>

        <Box ml="25%" mr="16%" >
          <InlineInputs gutter="medium">
            <FilterableSelect 
              size="medium"
              name="isLoading" 
              id="isLoading" 
              label="Depot:" 
              onChange={handleChangedDepot} 
              value={selectedDepot}>
              {
                allDepots && allDepots.length > 0 ?
                  allDepots.map((element, index) => {
                    return (
                      <Option text={element.text} value={element.value} key={index} />
                    )
                  })
                : 
                null
              }
            </FilterableSelect>
                 
            <DateInput 
              size="medium"
              maxWidth="30%"
              ml="100px"
              //autofocus
              label = "Date Start" 
              name = "date-input1" 
              value={dateFrom} 
              icon={<Icon type='add'/>}
              onChange = {onChangeSetDateFrom}> 
            </DateInput>
                
            <DateInput 
              size="medium"
              maxWidth="30%"
              ml="25px"
              //autofocus
              label = "Date To" 
              name = "date-input1" 
              value={dateTo} 
              icon={<Icon type='add'/>}
              onChange = {onChangeSetDateTo}> 
            </DateInput>
          
          </InlineInputs>
        </Box>

        <div className = "buttonTable">
          <ButtonBar fullWidth ml={2} mt={2}>
            <Button buttonType="primary" onClick={handleGetOrders} disabled={disableImportJobsButton}>
              {importJobsButtonText}
            </Button>
            <Button buttonType="primary" onClick={handleUploadOrders} disabled={disableUploadJobsButton}>
              {uploadJobsButtonText}
            </Button>
          </ButtonBar> 
        </div>

        <div id = "Tables" className="Tables">
          <div id = "importResponseTable">
            <div className = "StatusMessageBox">
                <DefaultMessage messageOptions={errorMessageBoxOptions} />
            </div> 
            {
              // listOfAllOrdersPulledIn && listOfAllOrdersPulledIn.length > 0 ?
              (showSuccessTable && showSuccessTable == true) ?
              <div id="SuccessTable">
                <div className="MessageBox">
                  <h3>Retrieved {listOfAllOrdersPulledIn.length} drops to upload </h3>
                </div>
                <div className="myTable">
                  <Table columns = {MainResponseColumns} data = {listOfAllOrdersPulledIn} /> 
                </div>
              </div>
              :
              null
            }
            {
              (showGettingRealitexMessage && showGettingRealitexMessage == true) ?
              <div id="GettingDropsFromRealitex" className="">
                <div className="MessageBox">
                  <h3>Retrieving drops from Realitex...</h3>
                </div>
              </div>
              :
              null
            }
            {
              (showUploadingToMaxOptraMessage && showUploadingToMaxOptraMessage == true) ?
              <div id="UploadingToMaxOptra" className="">
                <div className="MessageBox">
                  <h3>Uploading drops to MaxOptra...</h3>
                </div>
              </div>
              :
              null
            }
            {
              listOfAllOrdersErrorsPulledIn && listOfAllOrdersErrorsPulledIn.length > 0 ?
              <div id="ErrorFromOrdersTable">
                <div className="MessageBox">
                  <h3>Retrieved {listOfAllOrdersErrorsPulledIn.length} Errors </h3>
                </div>
                <div className="myTable">
                  <Table columns = {ErrorResponseColumns} data = {listOfAllOrdersErrorsPulledIn} /> 
                </div>
              </div>
              :
              null
            }
            {
              listOfAllErrorsFromOrderCreation && listOfAllErrorsFromOrderCreation.length > 0 ?
              <div id="ErrorOrderCreationTable">
                <div className="MessageBox">
                  <h3>Retrieved {listOfAllErrorsFromOrderCreation.length} Errors </h3>
                </div>
                <div className="myTable">
                  <Table columns = {ErrorResponseOrderCreationColumns} data = {listOfAllErrorsFromOrderCreation} /> 
                </div>
              </div>
              :
              null
            }
          </div>       
        </div>
      </Form>
    )
  }; export default RequestPage;
